import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TIMEZONE, addDays, fmtDec, fmtTime, getRange, ldnCompose, londonDateStr, projectColor } from '@foodstyles/shared';
import { useAuth } from '../../context/AuthContext';
import { useBootstrap } from '../../hooks/useBootstrap';
import { useNow } from '../../context/NowContext';
import { apiGet, qs } from '../../lib/api';
import { Card, Select, Spinner } from '../../components/primitives';
import type { DetailedResponse, DetailedRow } from '../../types/api';

const HOUR_H = 48; // px per hour row
const MIN_BLOCK_H = 20;

const _dayHdr = new Intl.DateTimeFormat('en-GB', { timeZone: TIMEZONE, weekday: 'short', day: 'numeric', month: 'short' });
const dayHeader = (key: string): string => _dayHdr.format(ldnCompose(key, '12:00'));
const hourLabel = (h: number): string => (h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`);

// Wall-clock fractional hour (0–24) of an instant in London — DST-correct (reuses the
// app's London time formatter), so a 09:00 entry is hour 9 even on the DST-transition days.
const hourOf = (ms: number): number => {
  const [h, m] = fmtTime(ms).split(':').map(Number);
  return h + m / 60;
};
const endHourOf = (start: number, stop: number): number =>
  londonDateStr(0, stop) === londonDateStr(0, start) ? hourOf(stop) : 24; // clamp entries that cross midnight

interface Placed {
  row: DetailedRow;
  startH: number;
  endH: number;
  col: number;
  cols: number;
}

// Lay a day's entries into side-by-side columns so overlapping entries don't cover
// each other (Google-Calendar style: cluster transitively-overlapping entries, then
// give each cluster as many columns as it needs).
function layoutDay(rows: DetailedRow[], now: number): Placed[] {
  const items = rows
    .map((row) => {
      const startH = hourOf(row.start);
      const rawEndH = endHourOf(row.start, row.stop ?? now);
      // Reserve at least the rendered min-height so short, sequential same-column
      // entries get pushed to separate columns instead of visually overlapping.
      const endH = Math.max(rawEndH, startH + MIN_BLOCK_H / HOUR_H);
      return { row, startH, endH };
    })
    .sort((a, b) => a.startH - b.startH || a.endH - b.endH);

  const placed: Placed[] = [];
  let cluster: Placed[] = [];
  let colEnds: number[] = [];
  let clusterEnd = -Infinity;
  const flush = () => {
    for (const p of cluster) p.cols = colEnds.length;
    cluster = [];
    colEnds = [];
    clusterEnd = -Infinity;
  };
  for (const it of items) {
    if (cluster.length && it.startH >= clusterEnd) flush();
    let c = colEnds.findIndex((end) => end <= it.startH);
    if (c === -1) {
      c = colEnds.length;
      colEnds.push(it.endH);
    } else {
      colEnds[c] = it.endH;
    }
    const p: Placed = { row: it.row, startH: it.startH, endH: it.endH, col: c, cols: 1 };
    cluster.push(p);
    placed.push(p);
    clusterEnd = Math.max(clusterEnd, it.endH);
  }
  flush();
  return placed;
}

export function CalendarView() {
  const { user } = useAuth();
  const boot = useBootstrap();
  const now = useNow();
  const users = boot.data?.users ?? [];
  // Canonical, unfiltered project order — the same one the Dashboard's colors key off,
  // so a project is the same colour here and there for every viewer.
  const orderedIds = boot.data?.projectOrder ?? [];

  const [member, setMember] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);
  const memberId = member || user?.id || '';

  const { from, to } = useMemo(() => getRange('week', Date.now(), undefined, undefined, weekOffset), [weekOffset]);
  const dayKeys = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(londonDateStr(0, from), i)), [from]);

  const q = useQuery({
    queryKey: ['report', 'calendar', memberId, from, to],
    queryFn: () => apiGet<DetailedResponse>(`/api/reports/detailed${qs({ from, to, member: memberId })}`),
    enabled: !!memberId,
  });
  const rows = q.data?.rows ?? [];

  const byDay = useMemo(() => {
    const m = new Map<string, DetailedRow[]>();
    for (const r of rows) {
      const k = londonDateStr(0, r.start);
      const arr = m.get(k);
      if (arr) arr.push(r);
      else m.set(k, [r]);
    }
    return m;
  }, [rows]);

  // Visible hour window, fit to the week's data (fallback to a 8am–6pm working day).
  const [minH, maxH] = useMemo<[number, number]>(() => {
    if (rows.length === 0) return [8, 18];
    let lo = 24;
    let hi = 0;
    for (const r of rows) {
      lo = Math.min(lo, hourOf(r.start));
      hi = Math.max(hi, endHourOf(r.start, r.stop ?? now));
    }
    const floorLo = Math.max(0, Math.floor(lo));
    return [floorLo, Math.min(24, Math.max(Math.ceil(hi), floorLo + 1))];
  }, [rows, now]);

  const hours = useMemo(() => Array.from({ length: maxH - minH }, (_, i) => minH + i), [minH, maxH]);
  const gridHeight = (maxH - minH) * HOUR_H;
  const dayTotal = (key: string): number => (byDay.get(key) ?? []).reduce((s, r) => s + r.durMs, 0);
  const weekLabel =
    weekOffset === 0 ? 'This week' : weekOffset === -1 ? 'Last week' : `${dayHeader(dayKeys[0])} – ${dayHeader(dayKeys[6])}`;

  return (
    <Card pad={false} className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3 dark:border-slate-700">
        <Select value={memberId} onChange={(e) => setMember(e.target.value)} className="w-56">
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
              {u.id === user?.id ? ' (me)' : ''}
            </option>
          ))}
        </Select>
        <div className="ml-auto flex items-center gap-1">
          <span className="mr-1 text-sm font-semibold text-slate-600 dark:text-slate-300">{weekLabel}</span>
          <button
            onClick={() => setWeekOffset((o) => o - 1)}
            aria-label="Previous week"
            className="rounded p-1.5 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => setWeekOffset((o) => Math.min(0, o + 1))}
            disabled={weekOffset >= 0}
            aria-label="Next week"
            className="rounded p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-700"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {q.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[760px]">
            {/* Day headers */}
            <div className="flex border-b border-slate-100 dark:border-slate-700">
              <div className="w-14 shrink-0" />
              {dayKeys.map((key) => (
                <div key={key} className="flex-1 border-l border-slate-100 px-2 py-2 text-center dark:border-slate-700">
                  <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">{dayHeader(key)}</div>
                  <div className="font-mono text-[11px] text-slate-400">{fmtDec(dayTotal(key))}</div>
                </div>
              ))}
            </div>
            {/* Grid */}
            <div className="flex">
              <div className="w-14 shrink-0">
                {hours.map((h) => (
                  <div key={h} style={{ height: HOUR_H }} className="relative">
                    <span className="absolute -top-1.5 right-1.5 text-[10px] text-slate-400">{hourLabel(h)}</span>
                  </div>
                ))}
              </div>
              {dayKeys.map((key) => {
                const placed = layoutDay(byDay.get(key) ?? [], now);
                return (
                  <div
                    key={key}
                    className="relative flex-1 border-l border-slate-100 dark:border-slate-700"
                    style={{ height: gridHeight }}
                  >
                    {hours.map((h) => (
                      <div key={h} style={{ height: HOUR_H }} className="border-t border-slate-100/70 dark:border-slate-700/50" />
                    ))}
                    {placed.map((p) => {
                      const c = p.row.projectId ? projectColor(p.row.projectId, orderedIds) : '#94a3b8';
                      const top = (p.startH - minH) * HOUR_H;
                      const height = Math.max(MIN_BLOCK_H, (Math.min(p.endH, maxH) - p.startH) * HOUR_H);
                      const widthPct = 100 / p.cols;
                      return (
                        <div
                          key={p.row.id}
                          className="absolute overflow-hidden rounded-md px-1.5 py-0.5 text-[11px] leading-tight ring-1 ring-black/5 dark:ring-white/10"
                          style={{
                            top,
                            height: height - 2,
                            left: `calc(${p.col * widthPct}% + 1px)`,
                            width: `calc(${widthPct}% - 2px)`,
                            background: `${c}22`,
                            borderLeft: `3px solid ${c}`,
                          }}
                          title={`${p.row.description || '(no description)'} · ${[p.row.project, p.row.task].filter(Boolean).join(' / ')} · ${fmtDec(p.row.durMs)}h`}
                        >
                          <div className="truncate font-medium text-slate-800 dark:text-slate-100">
                            {p.row.description || '(no description)'}
                          </div>
                          {height > 32 && p.row.task && (
                            <div className="truncate text-slate-500 dark:text-slate-400">{p.row.task}</div>
                          )}
                          {height > 48 && (
                            <div className="mt-0.5 font-mono text-[10px] text-slate-500 dark:text-slate-400">{fmtDec(p.row.durMs)}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            {rows.length === 0 && (
              <p className="py-6 text-center text-sm text-slate-400">No time tracked this week.</p>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
